import { useContext } from 'react';
import { TenantContext } from '../../../context/tenant-context';
import { handleRequest } from '../quipli-service-client';
import {
  CreateStripeAccountRequest,
  PaymentMethodConfig,
  PaymentMethodRequest,
  PaymentMethodSearchRequest,
  StripeAccount,
} from './payment-config';
import { PaymentConfigParsers } from './payment-config-parsers';
import PaymentMethodJson = PaymentConfigParsers.PaymentMethodJson;
import { PaymentContext } from '../../../context/payment-context';

export const usePaymentConfigApiV1 = () => {
  const { tenant, contextPath } = useContext(TenantContext);

  async function getStripeAccount(): Promise<StripeAccount | undefined> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/payment-config/stripe`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const result = await handleRequest<StripeAccount | undefined>(config);
    return result && PaymentConfigParsers.toStripeAccount(result);
  }

  async function addStripeAccount(req: CreateStripeAccountRequest): Promise<StripeAccount> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/payment-config/stripe`,
      data: { ...req },
      headers: {
        'X-Tenant': tenant,
      },
    };

    const result = await handleRequest<StripeAccount>(config);
    return PaymentConfigParsers.toStripeAccount(result);
  }

  async function getStripeAuthUrl(accountId: string): Promise<{ url: string }> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/payment-config/stripe/authUrl/${accountId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<{ url: string }>(config);
  }

  async function authorizeStripeAccount(authCode: string): Promise<{ url: string }> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/payment-config/stripe/authorizeAccount/${authCode}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<{ url: string }>(config);
  }

  async function searchPaymentMethods(req: PaymentMethodSearchRequest): Promise<PaymentMethodConfig[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/payment-config/methods/search`,
      data: { ...req },
      headers: {
        'X-Tenant': tenant,
      },
    };

    const result = await handleRequest<PaymentMethodJson[]>(config);
    return result.map(PaymentConfigParsers.toPaymentMethod);
  }

  async function createPaymentMethod(req: PaymentMethodRequest): Promise<PaymentMethodConfig> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/payment-config/methods/create`,
      data: { ...req },
      headers: {
        'X-Tenant': tenant,
      },
    };

    const result = await handleRequest<PaymentMethodJson>(config);
    return PaymentConfigParsers.toPaymentMethod(result);
  }

  async function updatePaymentMethod(req: PaymentMethodConfig): Promise<PaymentMethodConfig> {
    const config = {
      method: 'patch',
      url: `${contextPath}quipli-service/v1/payment-config/methods/${req.id}`,
      data: { ...req },
      headers: {
        'X-Tenant': tenant,
      },
    };

    const result = await handleRequest<PaymentMethodJson>(config);
    return PaymentConfigParsers.toPaymentMethod(result);
  }

  async function deletePaymentMethod(id: string): Promise<void> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/v1/payment-config/methods/${id}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    await handleRequest<void>(config);
  }

  async function getContext(): Promise<PaymentContext> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/payment-config/context`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<PaymentContext>(config);
  }

  return {
    getStripeAccount,
    getStripeAuthUrl,
    addStripeAccount,
    authorizeStripeAccount,
    searchPaymentMethods,
    createPaymentMethod,
    updatePaymentMethod,
    deletePaymentMethod,
    getContext,
  };
};
